const ALGO = 'AES-GCM'
const KEY_BITS = 256
const PBKDF2_ITER = 200_000

function b64ToBuffer(b64: string): ArrayBuffer {
  const str = atob(b64)
  const buf = new ArrayBuffer(str.length)
  const view = new Uint8Array(buf)
  for (let i = 0; i < str.length; i++) view[i] = str.charCodeAt(i)
  return buf
}

function bufToB64(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
}

async function deriveKey(password: string, salt: ArrayBuffer): Promise<CryptoKey> {
  const raw = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey']
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITER, hash: 'SHA-256' },
    raw,
    { name: ALGO, length: KEY_BITS },
    false,
    ['encrypt', 'decrypt']
  )
}

export interface EncryptedPayload {
  data: string
  salt: string
  iv: string
}

export async function encryptText(plaintext: string, password: string): Promise<EncryptedPayload> {
  const saltBuf = crypto.getRandomValues(new Uint8Array(16)).buffer as ArrayBuffer
  const ivBuf = crypto.getRandomValues(new Uint8Array(12)).buffer as ArrayBuffer
  const key = await deriveKey(password, saltBuf)
  const ciphertext = await crypto.subtle.encrypt({ name: ALGO, iv: ivBuf }, key, new TextEncoder().encode(plaintext))
  return { data: bufToB64(ciphertext), salt: bufToB64(saltBuf), iv: bufToB64(ivBuf) }
}

export async function decryptText(payload: EncryptedPayload, password: string): Promise<string> {
  const key = await deriveKey(password, b64ToBuffer(payload.salt))
  const plain = await crypto.subtle.decrypt(
    { name: ALGO, iv: b64ToBuffer(payload.iv) },
    key,
    b64ToBuffer(payload.data)
  )
  return new TextDecoder().decode(plain)
}
